import { useCallback } from "react";
import { authHeaders, type TokenSource } from "./api";
import { firebaseTokenSource } from "./auth-token";
import { useClients } from "./clients";

/**
 * The api's plain-HTTP OAuth start (architecture §5): fetch `{url}` with the ID token, then the
 * browser navigates there. `path` is relative to the api origin, e.g. `meetConsentPath(...)`.
 */
export async function consentUrl(apiUrl: string, getToken: TokenSource, path: string, fetchImpl: typeof fetch = fetch): Promise<string> {
  const res = await fetchImpl(`${apiUrl.replace(/\/+$/, "")}${path}`, { headers: await authHeaders(getToken) });
  const body: unknown = await res.json();
  if (!res.ok) {
    const reason = (body as { error?: { reason?: unknown } }).error?.reason;
    throw new Error(typeof reason === "string" ? `Could not start Google consent (${reason}).` : `Could not start Google consent (${res.status}).`);
  }
  const url = (body as { url?: unknown }).url;
  if (typeof url !== "string" || !url.startsWith("https://")) throw new Error("The api returned no consent URL.");
  return url;
}

/** Starts a consent flow and leaves the page for Google's consent screen. */
export function useStartConsent(): (path: string) => Promise<void> {
  const { config, firebase } = useClients();
  return useCallback(
    async (path: string) => {
      const url = await consentUrl(config.apiUrl, firebaseTokenSource(firebase.auth), path);
      window.location.assign(url);
    },
    [config.apiUrl, firebase.auth],
  );
}
